/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn, withErrorBoundary } from '@navi-app/utils';
import { Navigation } from 'baseui/side-navigation';
import { TFunction } from 'i18next';
import { ReactNode, forwardRef, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type DynamicObject = {
  [name: string]: any;
};

type TCard = {
  id: string;
  ref: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  children: ReactNode;
  className?: string;
  isError?: boolean;
  additionalElement?: ReactNode;
};

type TSideNavigation = {
  t: TFunction;
  defaultUrl: string;
  defaultActiveItem: string;
  sectionRefs: DynamicObject;
  cardDatas: TCard[];
  customClassCardWrapper?: string;
  customClass?: string;
  contentDisplay?: 'scroll' | 'section';
  sidebarClass?: string;
};

type TProps = {
  children: ReactNode;
  id?: string;
  className?: string;
};

const Sidebar = ({
  sectionRefs,
  defaultUrl,
  defaultActiveItem,
  navigationItems,
  contentDisplay,
}: any) => {
  const navigate = useNavigate();
  const [activeItemId, setActiveItemId] = useState<string | undefined>(
    `${defaultUrl}#${defaultActiveItem}`
  );

  useEffect(() => {
    const sections = Object?.keys(sectionRefs.current);

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveItemId(`${defaultUrl}#${entry.target.id}`);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '0px',
      threshold: 0.5,
    });

    sections.forEach((section) => {
      if (sectionRefs.current[section]) {
        observer.observe(sectionRefs.current[section]);
      }
    });

    return () => {
      sections.forEach((section) => {
        if (sectionRefs.current[section]) {
          observer.unobserve(sectionRefs.current[section]);
        }
      });
    };
  }, [sectionRefs]);

  return (
    <Navigation
      items={navigationItems}
      activeItemId={activeItemId}
      onChange={({ event, item }) => {
        setActiveItemId(item.itemId);
        if (contentDisplay !== 'scroll') {
          event.preventDefault();
          navigate(item.itemId as string, { replace: true });
        }
      }}
    />
  );
};

const SectionWrapper = forwardRef((props: any, ref) => (
  <div
    id={props?.id}
    // @ts-expect-error
    ref={ref}
  >
    {props?.children}
  </div>
));

const Card = (props: TProps) => {
  return (
    <div
      className={cn('border border-[#E8E8E8] rounded-xl p-6', props?.className)}
      id={props?.id}
    >
      {props?.children}
    </div>
  );
};

const SideNavigationComponent: React.FC<TSideNavigation> = ({
  t,
  defaultUrl,
  sectionRefs,
  defaultActiveItem,
  cardDatas,
  customClassCardWrapper,
  customClass,
  contentDisplay = 'scroll',
  sidebarClass,
}: TSideNavigation) => {
  const location = useLocation();

  const navigationItems = cardDatas?.map((c: any) => {
    return {
      title: (
        <span>
          {t(c?.id)}
          {c.isError && <span className="ml-1 text-[#E11900]">*</span>}
        </span>
      ),
      itemId: `${defaultUrl}#${c?.id}`,
    };
  });

  const selectedCard = useMemo(() => {
    const locationHash = location.hash || cardDatas?.[0]?.id;
    return cardDatas?.find((c: any) => {
      return `#${c?.id}`.includes(locationHash);
    });
  }, [cardDatas]) as TCard;

  return (
    <div className={`${customClass || 'flex flex-col md:flex-row gap-4'}`}>
      {/* Sidebar Navigation */}
      <div
        className={cn(
          'w-full md:w-[230px] md:flex-shrink-0 md:flex md:flex-col',
          sidebarClass
        )}
      >
        <Sidebar
          sectionRefs={sectionRefs}
          defaultUrl={defaultUrl}
          defaultActiveItem={defaultActiveItem}
          navigationItems={navigationItems}
          contentDisplay={contentDisplay}
        />
      </div>
      {/* Content Section */}
      {contentDisplay === 'scroll' ? (
        <div
          className={cn(
            customClassCardWrapper,
            'flex flex-col flex-1 gap-4 overflow-auto no-scrollbar scroll-smooth'
          )}
        >
          {cardDatas?.map((c: any) => (
            <SectionWrapper
              id={c.id}
              // @ts-ignore
              ref={c.ref}
            >
              <Card className={c.className}>{c.children}</Card>
            </SectionWrapper>
          ))}
        </div>
      ) : (
        <div className={cn('w-full', customClassCardWrapper)}>
          {selectedCard.additionalElement}
          <SectionWrapper>
            <Card className={selectedCard?.className}>
              {selectedCard?.children}
            </Card>
          </SectionWrapper>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(SideNavigationComponent);
